import React, { useState, useEffect } from 'react';
import { Search, AlertTriangle, Clock, MapPin, User, Car, Eye, CheckCircle, XCircle, RefreshCw, X } from 'lucide-react';
import axios from 'axios';

interface Emergency {
  id: number;
  type: string;
  message?: string;
  crash_type?: string;
  severity?: string;
  user_username: string;
  user_name?: string;
  family_name: string;
  latitude?: number;
  longitude?: number;
  created_at: string;
  is_active?: boolean;
}

const Emergencies: React.FC = () => {
  const [emergencies, setEmergencies] = useState<Emergency[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selectedEmergency, setSelectedEmergency] = useState<Emergency | null>(null);

  useEffect(() => {
    fetchEmergencies();
  }, []);

  const fetchEmergencies = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get('https://api.lifeshare.app/api/v1/admin/emergencies');
      setEmergencies(response.data);
    } catch (error) {
      console.error('Emergencies fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleToggleStatus = async (emergencyId: number, currentStatus: boolean) => {
    if (window.confirm(`Bu acil durumu ${currentStatus ? 'pasif' : 'aktif'} yapmak istediğinizden emin misiniz?`)) {
      setActionLoading(emergencyId);
      try {
        await axios.patch(`https://api.lifeshare.app/api/v1/emergency/${emergencyId}`, { is_active: !currentStatus });
        setEmergencies(emergencies.map(e => e.id === emergencyId ? { ...e, is_active: !currentStatus } : e));
      } catch (error: any) {
        alert('İşlem başarısız: ' + (error.response?.data?.detail || 'Bir hata oluştu'));
      }
      setActionLoading(null);
    }
  };

  const handleViewLocation = (emergency: Emergency) => {
    if (emergency.latitude && emergency.longitude) {
      window.open(`https://www.google.com/maps?q=${emergency.latitude},${emergency.longitude}`, '_blank');
    }
  };

  const filteredEmergencies = emergencies.filter(emergency => {
    const matchesSearch = emergency.type.toLowerCase().includes(searchText.toLowerCase()) ||
      (emergency.message && emergency.message.toLowerCase().includes(searchText.toLowerCase())) ||
      emergency.user_username.toLowerCase().includes(searchText.toLowerCase()) ||
      emergency.family_name.toLowerCase().includes(searchText.toLowerCase());
    const matchesActiveFilter = filterActive === null || emergency.is_active === filterActive;
    const matchesTypeFilter = filterType === 'all' || emergency.type === filterType;
    return matchesSearch && matchesActiveFilter && matchesTypeFilter;
  }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const uniqueTypes = Array.from(new Set(emergencies.map(e => e.type)));
  const activeCount = emergencies.filter(e => e.is_active).length;
  const resolvedCount = emergencies.filter(e => !e.is_active).length;

  const getTypeColor = (type: string) => {
    if (type === 'crash') return 'bg-red-100 text-red-700';
    if (type === 'sos') return 'bg-orange-100 text-orange-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  const getTypeIcon = (type: string) => {
    if (type === 'crash') return <Car className="w-3.5 h-3.5" />;
    return <AlertTriangle className="w-3.5 h-3.5" />;
  };

  const getTypeText = (type: string) => {
    if (type === 'crash') return 'Kaza';
    if (type === 'sos') return 'SOS';
    return 'Acil Durum';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Acil Durumlar</h1>
            <p className="text-sm text-gray-500 mt-1">Sistemdeki acil durumları yönetin</p>
          </div>
          <button onClick={fetchEmergencies} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
            <p className="text-2xl font-bold text-purple-700">{emergencies.length}</p>
            <p className="text-xs text-purple-600">Toplam</p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-4">
            <p className="text-2xl font-bold text-red-700">{activeCount}</p>
            <p className="text-xs text-red-600">Aktif</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
            <p className="text-2xl font-bold text-green-700">{resolvedCount}</p>
            <p className="text-xs text-green-600">Çözüldü</p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Acil durum, kullanıcı veya çevre ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setFilterActive(null)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filterActive === null ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Tümü</button>
            <button onClick={() => setFilterActive(true)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filterActive === true ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>🔴 Aktif</button>
            <button onClick={() => setFilterActive(false)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filterActive === false ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>✓ Çözüldü</button>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-4 py-2 bg-gray-100 rounded-full text-sm outline-none">
              <option value="all">Tüm Tipler</option>
              {uniqueTypes.map(type => <option key={type} value={type}>{getTypeText(type)}</option>)}
            </select>
          </div>
        </div>

        {/* Emergencies List */}
        {filteredEmergencies.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Acil durum bulunamadı</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEmergencies.slice(0, 50).map((emergency) => (
              <div key={emergency.id} className={`bg-white rounded-2xl p-4 hover:ring-2 transition-all ${emergency.is_active ? 'hover:ring-red-200 border-l-4 border-red-500' : 'hover:ring-purple-100'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${emergency.is_active ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                      {getTypeIcon(emergency.type)}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-xl text-xs font-medium ${getTypeColor(emergency.type)}`}>
                          {getTypeIcon(emergency.type)}
                          {getTypeText(emergency.type)}
                        </span>
                        {emergency.severity && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-xl text-xs font-medium">{emergency.severity.toUpperCase()}</span>
                        )}
                        {emergency.is_active && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-xl text-xs font-medium animate-pulse">🔴 AKTİF</span>
                        )}
                        <span className="text-xs text-gray-400">#{emergency.id}</span>
                      </div>

                      {emergency.message && <p className="text-sm text-gray-900 mb-2 truncate">{emergency.message}</p>}

                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{emergency.user_name || emergency.user_username}</span>
                        <span>� {emergency.family_name}</span>
                        {emergency.latitude && emergency.longitude && (
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Konum var</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(emergency.created_at).toLocaleString('tr-TR')}
                    </div>
                    <div className="flex items-center gap-1">
                      {actionLoading === emergency.id ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-purple-300 border-t-purple-600" />
                      ) : (
                        <>
                          {emergency.latitude && emergency.longitude && (
                            <button onClick={() => handleViewLocation(emergency)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-xl"><MapPin className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => setSelectedEmergency(emergency)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-xl"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => handleToggleStatus(emergency.id, emergency.is_active || false)} className={`p-1.5 rounded-xl ${emergency.is_active ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}`}>
                            {emergency.is_active ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        {filteredEmergencies.length > 50 && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">Gösterilen: 50 / {filteredEmergencies.length}</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedEmergency && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedEmergency(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${selectedEmergency.is_active ? 'bg-red-100' : 'bg-green-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${selectedEmergency.is_active ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Acil Durum #{selectedEmergency.id}</h2>
              </div>
              <button onClick={() => setSelectedEmergency(null)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className={`p-4 rounded-xl ${selectedEmergency.is_active ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${selectedEmergency.is_active ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />
                  <span className={`font-medium ${selectedEmergency.is_active ? 'text-red-800' : 'text-green-800'}`}>
                    {selectedEmergency.is_active ? 'AKTİF ACİL DURUM' : 'ÇÖZÜLDÜ'}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <span className={`px-3 py-1 rounded-xl text-sm font-medium ${getTypeColor(selectedEmergency.type)}`}>{getTypeText(selectedEmergency.type)}</span>
                {selectedEmergency.severity && <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-xl text-sm font-medium">{selectedEmergency.severity.toUpperCase()}</span>}
              </div>

              {selectedEmergency.message && (
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Mesaj</p>
                  <p className="text-gray-900">{selectedEmergency.message}</p>
                </div>
              )}

              {selectedEmergency.crash_type && (
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Kaza Türü</p>
                  <p className="text-gray-900">{selectedEmergency.crash_type}</p>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-2">Kullanıcı</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {(selectedEmergency.user_name || selectedEmergency.user_username).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedEmergency.user_name || selectedEmergency.user_username}</p>
                    <p className="text-xs text-gray-500">@{selectedEmergency.user_username} • � {selectedEmergency.family_name}</p>
                  </div>
                </div>
              </div>

              {selectedEmergency.latitude && selectedEmergency.longitude && (
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <p className="text-xs text-blue-600 mb-2">Konum</p>
                  <p className="text-sm text-gray-900 mb-3">{selectedEmergency.latitude.toFixed(6)}, {selectedEmergency.longitude.toFixed(6)}</p>
                  <button onClick={() => handleViewLocation(selectedEmergency)} className="w-full py-2.5 bg-purple-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-purple-700">
                    <MapPin className="w-4 h-4" />Haritada Görüntüle
                  </button>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Oluşturulma Zamanı</p>
                <p className="text-gray-900">{new Date(selectedEmergency.created_at).toLocaleString('tr-TR')}</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex gap-2">
              <button onClick={() => { handleToggleStatus(selectedEmergency.id, selectedEmergency.is_active || false); setSelectedEmergency(null); }} className={`flex-1 py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 ${selectedEmergency.is_active ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                {selectedEmergency.is_active ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {selectedEmergency.is_active ? 'Çözüldü Olarak İşaretle' : 'Aktif Olarak İşaretle'}
              </button>
              <button onClick={() => setSelectedEmergency(null)} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emergencies;
